import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import { useGetIndividualsByEntityIdQuery, useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { useSelector } from "react-redux";
import {
  getIndividualByUserId,
  getIndividualsByEntity,
} from "marketplace/individuals/_stores/individuals-redux-slice";

const Container = styled(FormControl)(() => ({
  maxWidth: '100%',
  minWidth: '33%'
}));

type CompanyProfileContactPersonInputProps = {
  className?: string;
  required?: boolean;
  contactUserId?: Guid;
  onChange: (value: ContactPersonOption | null) => void;
};

type ContactPersonOption = Individual | IndividualSummary;

export default function CompanyProfileContactPersonInput(
  props: Readonly<CompanyProfileContactPersonInputProps>) {
  const {
    className,
    required,
    contactUserId,
    onChange
  } = props;

  // #region Hooks
  const session = useSession();

  const legalEntityId = session.user!.legalEntityId;

  // #region Selectors
  const coworkers = useSelector(getIndividualsByEntity(legalEntityId));
  const { isLoading: isLoadingCoworkers } = useGetIndividualsByEntityIdQuery({ legalEntityId: legalEntityId.value });
  const contactUserIds = useMemo(() => new Set(contactUserId ? [contactUserId.value] : []), [contactUserId]);
  const { isLoading: isLoadingContactPerson } = useGetIndividualsByUserIdsQuery(
    { userIds: contactUserIds },
    { skip: !contactUserId },
  );
  const contactPersonByUserId = useSelector(getIndividualByUserId(contactUserId));

  const options = useMemo<ContactPersonOption[]>(() => {
    const coworkerOptions = coworkers ?? [];
    if (!contactPersonByUserId) return coworkerOptions;
    const contactIsAlreadyIncluded = coworkerOptions.some((coworker) =>
      coworker.userId.isEqualTo(contactPersonByUserId.userId),
    );
    return contactIsAlreadyIncluded ? coworkerOptions : [contactPersonByUserId, ...coworkerOptions];
  }, [contactPersonByUserId, coworkers]);

  // Derive contactPerson directly from coworkers and contactUserId
  const contactPerson = useMemo(() => {
    if (!contactUserId) return null;
    return options.find((contactOption) => contactOption.userId.isEqualTo(contactUserId)) ?? null;
  }, [contactUserId, options]);
  // #endregion

  const filterOptions = useMemo(
    () =>
      createFilterOptions<ContactPersonOption>({
        stringify: (contactOption) =>
          `${contactOption.firstName} ${contactOption.lastName} ${getContactEmail(contactOption) ?? ""}`,
      }),
    []
  );

  // #region Render
  return (
    <Container className={className}>
      <Autocomplete
        disablePortal
        getOptionLabel={getOptionLabel}
        getOptionKey={(option) => option.id.value}
        onChange={(_event, value) => {
          onChange(value);
        }}
        value={contactPerson}
        options={options}
        isOptionEqualToValue={(option: unknown, value: unknown) => {
          if (!option || !value) return false;
          const coworker = option as ContactPersonOption;
          const selectedCoworker = value as ContactPersonOption;
          return coworker.id.isEqualTo(selectedCoworker.id);
        }}
        loading={isLoadingCoworkers || isLoadingContactPerson}
        filterOptions={filterOptions}
        noOptionsText="No matching coworkers found"
        renderOption={(liProps, coworker) => {
          const { key, children: _children, ...optionProps } = liProps;
          void _children;

          return (
            <li key={key} {...optionProps}>
              <ListItemAvatar>
                <Avatar>
                  {`${coworker.firstName.slice(0, 1)}${coworker.lastName.slice(0, 1)}`}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${coworker.firstName} ${coworker.lastName}`}
                secondary={getContactEmail(coworker)?.toString() ?? ""}
              />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="none"
            variant="outlined"
            required={required}
            label="Contact Person"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoadingCoworkers || isLoadingContactPerson
                      ? <CircularProgress color="inherit" size={20} disableShrink />
                      : null
                    }
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
    </Container>
  );
}

function getOptionLabel(option: unknown) {
  if (!option) return 'None';
  const coworker = option as ContactPersonOption;
  return `${coworker.firstName} ${coworker.lastName}`;
}

function getContactEmail(contactOption: ContactPersonOption) {
  return contactOption instanceof Individual ? contactOption.email : contactOption.emailAddress;
}
